import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { MeTenant, TenantRole } from '../models/api.models';
import { AuthService } from './auth.service';
import { CapabilitiesService } from './capabilities.service';
import { MeService } from './me.service';
import { ToastService } from './toast.service';

// Holds the caller's tenant list + active-tenant state.
// The JWT `active_tenant_id` claim is a hint: once the tenant list is loaded, a claim naming a tenant
// the caller is not in is ignored and repaired. Switches go through `AuthService.setActiveTenantClaim`.
@Injectable({ providedIn: 'root' })
export class ActiveTenantService {
  private readonly authService = inject(AuthService);
  private readonly capabilitiesService = inject(CapabilitiesService);
  private readonly meService = inject(MeService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);

  readonly tenants = signal<MeTenant[]>([]);
  readonly loaded = signal(false);

  /** The tenant the token names, whether or not the caller is still in it. */
  private readonly claimId = computed(() => this.authService.claims().activeTenantId ?? '');

  /** True once the loaded tenant list shows the claim names a tenant the caller is not in. */
  readonly claimIsStale = computed(() => {
    const claim = this.claimId();
    return this.loaded() && claim !== '' && !this.tenants().some((t) => t.id === claim);
  });

  // A valid claim wins. Without one (empty, or stale) the sole membership is used, mirroring
  // the backend's single-membership autoresolve in `RequireTenantScope`.
  readonly activeId = computed(() => {
    const claim = this.claimId();
    if (claim && !this.claimIsStale()) return claim;
    const t = this.tenants();
    if (t.length === 1) return t[0].id;
    return '';
  });

  readonly active = computed(() => this.tenants().find((t) => t.id === this.activeId()) ?? null);

  /** Caller's role inside the active tenant. */
  readonly activeTenantRole = computed<TenantRole | ''>(() => {
    const active = this.active();
    return active?.tenant_role ?? '';
  });

  /** True when the caller is in more than one tenant - drives switcher visibility. */
  readonly canSwitch = computed(() => this.tenants().length > 1);

  constructor() {
    // Clears stale capabilities and reloads fresh ones whenever activeId or userId changes.
    effect(() => {
      const tenantId = this.activeId();
      const userId = this.authService.claims().userId;
      this.capabilitiesService.clear();
      if (tenantId && userId) {
        this.capabilitiesService.loadForCaller(tenantId, userId);
      }
    });

    effect(() => {
      if (this.claimIsStale()) this.repairStaleClaim();
    });
  }

  /** The stale claim a repair was already started for, so one claim is repaired once. */
  private repairedClaim = '';

  // A token naming a tenant the caller left, or one that is gone, cannot be used: with one
  // membership the claim is moved to it, otherwise the caller picks.
  private repairStaleClaim(): void {
    const claim = this.claimId();
    if (claim === this.repairedClaim) return;
    this.repairedClaim = claim;
    const memberships = this.tenants();
    if (memberships.length === 1) {
      void this.switch(memberships[0].id);
    } else if (memberships.length > 1 && !this.router.url.startsWith('/tenant-picker')) {
      void this.router.navigateByUrl('/tenant-picker');
    }
  }

  /** Fetches the caller's tenant memberships; idempotent, silent on error. */
  loadTenants(): void {
    this.meService.listMyTenants().subscribe({
      next: (rows) => {
        this.tenants.set(rows);
        this.loaded.set(true);
      },
      error: () => {
        /* silent - see method doc */
      },
    });
  }

  // Sets active_tenant_id via backend+Keycloak Admin API then reloads; 403 = revoked membership.
  // `redirectTo` lands the reload on a specific page instead of the current one - needed when the
  // caller must switch AND move (e.g. into a soft-deleted tenant, where only Settings still works);
  // doing it as one hard navigation avoids a router hop that would race the reload.
  async switch(tenantId: string, redirectTo?: string): Promise<void> {
    // Compared with the token, not with `activeId`: a stale claim must be replaceable even by
    // the tenant the page already shows as active.
    if (tenantId === this.claimId()) {
      if (redirectTo) await this.router.navigateByUrl(redirectTo);
      return;
    }
    try {
      await this.authService.setActiveTenantClaim(tenantId);
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 403) {
        this.toastService.error('You no longer have access to this tenant. Refreshing your tenant list.');
        this.loadTenants();
      } else {
        this.toastService.error('Could not switch tenant. Please try again.');
      }
      return;
    }
    // Full reload ensures all components and guards re-run with the new
    // tenant JWT claim - simpler and safer than trying to recreate in-place.
    if (redirectTo) {
      globalThis.location.assign(redirectTo);
      return;
    }
    globalThis.location.reload();
  }
}
